package info.rezident.app.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import info.rezident.app.core.AppErrorMessages
import info.rezident.app.core.Logger
import info.rezident.app.data.DependencyInjection
import info.rezident.app.data.auth.AuthRepository
import info.rezident.app.data.quiz.QuizQuestion
import info.rezident.app.data.quiz.UserAnswer
import info.rezident.app.data.quiz.UserQuizRepository
import info.rezident.app.seo.setMetaNameTag
import info.rezident.app.ui.components.sections.NavHeader
import info.rezident.app.ui.components.widgets.Spacer
import info.rezident.app.ui.theme.AppTheme
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.rgba

@Page("/raspunsuri")
@Composable
fun SummaryPage() {
    LaunchedEffect(Unit) {
        document.title = "Rezumatul Quiz-ului"
        document.setMetaNameTag(name = "robots", content = "noindex, nofollow")
    }

    val pageContext = rememberPageContext()
    val quizId: String? = pageContext.route.params["quizId"]

    // Handle null quizId by redirecting to Quiz page
    LaunchedEffect(quizId) {
        if (quizId.isNullOrBlank()) {
            pageContext.router.navigateTo("/quiz")
            return@LaunchedEffect
        }
    }

    // Don't render if quizId is null (will redirect)
    if (quizId.isNullOrBlank()) {
        return
    }

    val viewModel = remember { DependencyInjection.quizViewModel }
    val questions by viewModel.questions.collectAsState()
    val steps by viewModel.steps.collectAsState()

    var answers by remember { mutableStateOf<List<UserAnswer>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var isNotFound by remember { mutableStateOf(false) }
    var isAnonymousOrNotSignedIn by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()
    LaunchedEffect(quizId) {
        isLoading = true
        isNotFound = false
        scope.launch {
            try {
                // Check authentication status
                val firebaseUser = AuthRepository.getCurrentUser()
                isAnonymousOrNotSignedIn = firebaseUser == null || firebaseUser.isAnonymous
                
                val quiz = UserQuizRepository.getCompletedQuizWithAnswers(quizId)
                if (quiz != null && quiz.answers.isNotEmpty()) {
                    answers = quiz.answers
                    // Load the correct template for this quiz
                    viewModel.loadTemplateById(quiz.templateId)
                    isNotFound = false
                } else {
                    isNotFound = true
                }
            } catch (e: Exception) {
                Logger.error("Failed to load answers for summary: ${e.message}")
                isNotFound = true
            } finally {
                isLoading = false
            }
        }
    }

    Column(Modifier.fillMaxSize(), horizontalAlignment = Alignment.CenterHorizontally) {
        NavHeaderSpacer()
        NavHeader(isSimple = true)

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(leftRight = 36.px, top = 24.px, bottom = 48.px)
                .maxWidth(1200.px),
            horizontalAlignment = Alignment.Start
        ) {
            if (isLoading) {
                SpanText(
                    text = "Se încarcă răspunsurile...",
                    modifier = AppTheme.typography.body1.toModifier().color(AppTheme.colors.textSecondary)
                )
                return@Column
            }

            if (isNotFound) {
                SummaryEmptyState(
                    isAnonymousOrNotSignedIn = isAnonymousOrNotSignedIn,
                    onAction = {
                        if (isAnonymousOrNotSignedIn) {
                            pageContext.router.navigateTo("/conectare")
                        } else {
                            pageContext.router.navigateTo("/quiz")
                        }
                    }
                )
                return@Column
            }

            SpanText(
                text = "Răspunsurile tale la Quiz-ul de Orientare",
                AppTheme.typography.h1.toModifier()
                    .margin(bottom = 16.px)
                    .color(AppTheme.colors.text)
            )

            SpanText(
                text = "Toate răspunsurile tale influențează recomandările pe care le primești. Poți vizualiza răspunsurile și recomandările tale oricând, din Profilul tău.",
                AppTheme.typography.body1.toModifier()
                    .margin(bottom = 16.px)
                    .color(AppTheme.colors.textSecondary)
            )

            Column(
                modifier = Modifier.fillMaxWidth().margin(bottom = 40.px)
            ) {
                steps.forEach { step ->
                    val stepQuestions = questions.filter { it.stepId == step.id }
                    val hasAnswersForStep = stepQuestions.any { question ->
                        answers.any { it.questionId == question.id }
                    }

                    if (hasAnswersForStep) {
                        SpanText(
                            text = step.title,
                            AppTheme.typography.h2.toModifier()
                                .margin(top = 24.px, bottom = 16.px)
                                .color(AppTheme.colors.text)
                        )

                        stepQuestions.forEach { question ->
                            val answer = answers.find { it.questionId == question.id }
                            if (answer != null) {
                                AnswerSummaryCard(
                                    question = question,
                                    answer = answer,
                                    modifier = Modifier.margin(bottom = 16.px)
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun SummaryStep() {
    val viewModel = remember { DependencyInjection.quizViewModel }
    val questions by viewModel.questions.collectAsState()
    val userAnswers by UserQuizRepository.userAnswers.collectAsState()
    val steps by viewModel.steps.collectAsState()

    // Start saving quiz answers in background when Summary step is displayed
    LaunchedEffect(Unit) {
        viewModel.startQuizSaving()
    }

    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.Start
    ) {
        // 1. Blurred area with placeholder visualizations
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Rezumatul Quiz-ului de Orientare",
                AppTheme.typography.h1.toModifier()
                    .margin(bottom = 16.px)
                    .color(AppTheme.colors.text)
            )

            SpanText(
                text = "Ești aproape! Verifică informațiile de mai jos ca să îți putem oferi cele mai bune recomandări pentru tine.",
                AppTheme.typography.body1.toModifier()
                    .margin(bottom = 16.px)
                    .color(AppTheme.colors.textSecondary)
            )

            // 2. Cards with user responses
            Column(
                modifier = Modifier.fillMaxWidth()
                    .margin(bottom = 40.px)
            ) {
                // Group answers by step
                steps.forEach { step ->
                    val stepQuestions = questions.filter { it.stepId == step.id }
                    val hasAnswersForStep = stepQuestions.any { question ->
                        userAnswers.answers.any { it.questionId == question.id }
                    }

                    if (hasAnswersForStep) {
                        // Step header
                        SpanText(
                            text = step.title,
                            AppTheme.typography.h2.toModifier()
                                .margin(top = 24.px, bottom = 16.px)
                                .color(AppTheme.colors.text)
                        )

                        // Step answers
                        stepQuestions.forEach { question ->
                            val answer = userAnswers.answers.find { it.questionId == question.id }
                            if (answer != null) {
                                AnswerSummaryCard(
                                    question = question,
                                    answer = answer,
                                    modifier = Modifier.margin(bottom = 16.px)
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AnswerSummaryCard(
    question: QuizQuestion,
    answer: UserAnswer,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .padding(16.px)
            .backgroundColor(AppTheme.colors.surface)
            .borderRadius(8.px)
            .boxShadow(offsetX = 0.px, offsetY = 2.px, blurRadius = 8.px, color = rgba(0, 0, 0, 0.1))
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(16.px)
        ) {
            // Factor text if present
            if (!question.factor.isNullOrBlank()) {
                SpanText(
                    text = question.factor,
                    AppTheme.typography.body2.toModifier()
                        .margin(bottom = 4.px)
                        .color(AppTheme.colors.textTertiary)
                )
            }
            
            // Question text
            SpanText(
                text = question.text,
                AppTheme.typography.body1Medium.toModifier()
                    .margin(bottom = 8.px)
                    .color(AppTheme.colors.text)
                    .fontWeight(FontWeight.Bold)
            )

            // Five-scale answer
            if (answer.fiveScaleAnswer != null) {
                SpanText(
                    text = "Răspuns: ${answer.fiveScaleAnswer} / 5",
                    AppTheme.typography.body1.toModifier()
                        .margin(top = 4.px)
                        .color(AppTheme.colors.textSecondary)
                        .fontWeight(FontWeight.SemiBold)
                )

                // Display scale explanation if present
                if (!question.scaleExplanation.isNullOrBlank()) {
                    SpanText(
                        text = question.scaleExplanation,
                        AppTheme.typography.body2.toModifier()
                            .margin(top = 4.px)
                            .color(AppTheme.colors.textTertiary)
                            .fontStyle(FontStyle.Italic)
                    )
                }
            }
            // Answer text
            else if (answer.answerIds != null && answer.answerIds.isNotEmpty()) {
                // For questions with predefined answers
                question.predefinedPredefinedAnswers
                    .filter { it.id in answer.answerIds }
                    .forEach { predefinedAnswer ->
                        SpanText(
                            text = predefinedAnswer.text,
                            AppTheme.typography.body1.toModifier()
                                .margin(top = 4.px)
                                .color(AppTheme.colors.textSecondary)
                        )
                    }
            }

            // Open text answer
            if (!answer.openAnswer.isNullOrBlank()) {
                SpanText(
                    text = answer.openAnswer,
                    AppTheme.typography.body1.toModifier()
                        .margin(top = 8.px)
                        .color(AppTheme.colors.textSecondary)
                        .fontStyle(FontStyle.Italic)
                )
            }
        }
    }
}

@Composable
private fun NavHeaderSpacer() {
    Spacer(
        navHeaderHeightStyle
            .toModifier()
            .id("acasa")
    )
}

@Composable
private fun SummaryEmptyState(
    isAnonymousOrNotSignedIn: Boolean,
    onAction: () -> Unit
) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier.fillMaxWidth().padding(64.px)
    ) {
        SpanText(
            text = "🤔",
            modifier = Modifier.fontSize(48.px).margin(bottom = 16.px)
        )
        SpanText(
            text = AppErrorMessages.QUIZ_NOT_FOUND_TITLE,
            modifier = Modifier.color(AppTheme.colors.text).margin(bottom = 8.px)
                .fontSize(24.px).fontWeight(600)
                .textAlign(TextAlign.Center)
        )
        SpanText(
            text = if (isAnonymousOrNotSignedIn) {
                AppErrorMessages.QUIZ_NOT_FOUND_ANONYMOUS_BODY
            } else {
                AppErrorMessages.QUIZ_NOT_FOUND_AUTHENTICATED_BODY
            },
            modifier = Modifier.color(AppTheme.colors.textSecondary).margin(bottom = 24.px)
                .textAlign(TextAlign.Center)
        )
        Button(
            onClick = { onAction() },
            modifier = Modifier
                .backgroundColor(Color.transparent)
                .padding(4.px)
                .border(1.px, LineStyle.Solid, color = AppTheme.colors.text)
                .borderRadius(AppTheme.shapes.mediumRound),
        ) {
            SpanText(
                text = if (isAnonymousOrNotSignedIn) {
                    AppErrorMessages.SIGN_IN_ACTION
                } else {
                    AppErrorMessages.START_NOW_ACTION
                },
                modifier = AppTheme.typography.body1Medium.toModifier()
                    .color(AppTheme.colors.text)
            )
        }
    }
}